import React, { useState } from 'react';
import { BarChart, Bar, XAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { Box, Typography, Select, MenuItem, FormControl, useTheme } from '@mui/material';

// const yearlyCountsList = [
//     { year: 2020, count: 15 },
//     { year: 2021, count: 5 },
//     { year: 2022, count: 0 },
//     { year: 2023, count: 20 },
//     { year: 2024, count: 10 },
// ];

interface YearData {
    year: number;
    count: number;
}

interface DroneDataYearDisplayProps {
    yearlyCountsList: YearData[];
    selectedYear: number | null;
    setSelectedYear: React.Dispatch<React.SetStateAction<number | null>>;
}


const ensureMinimumEntries = (yearlyCountsList: YearData[]): YearData[] => {
    const currentYear = new Date().getFullYear();
    let filledYearlyCountsList = [...yearlyCountsList];

    // Add years up until the current year with value zero
    for (let year = Math.min(...filledYearlyCountsList.map(item => item.year)); year <= currentYear; year++) {
        if (!filledYearlyCountsList.some(item => item.year === year)) {
            filledYearlyCountsList.push({ year, count: 0 });
        }
    }

    // Sort the list by year
    filledYearlyCountsList.sort((a, b) => a.year - b.year);

    // If still less than 3 entries, add previous years with value zero
    while (filledYearlyCountsList.length < 3) {
        const earliestYear = filledYearlyCountsList[0].year;
        filledYearlyCountsList.unshift({ year: earliestYear - 1, count: 0 });
    }

    return filledYearlyCountsList;
};

const DroneDataYearDisplay: React.FC<DroneDataYearDisplayProps> = ({ yearlyCountsList, selectedYear, setSelectedYear }) => {
    const theme = useTheme();
    const filledYearlyCountsList = ensureMinimumEntries(yearlyCountsList);

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: 600,
                height: 80,
                // padding: 1,
                // border: '1px solid #ccc',
                borderRadius: '8px',
                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                // backgroundColor: '#fff',
            }}
        >
            {/* Left section with title and dropdown */}
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '40%' }}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                    Choose year
                </Typography>
                <FormControl fullWidth size="small">
                    {/* <InputLabel id="year-select-label">Year</InputLabel> */}
                    <Select
                        labelId="year-select-label"
                        id="year-select"
                        value={selectedYear ? selectedYear : ""}
                        onChange={(e) => setSelectedYear(e.target.value as number)}
                        // label="Year"
                        sx={{ fontSize: 14 }}
                    >
                        {filledYearlyCountsList.length === 0 ? (
                            <MenuItem disabled>No years available</MenuItem>
                        ) : (
                            filledYearlyCountsList.map((item) => (
                                <MenuItem key={item.year} value={item.year} disabled={item.count === 0}>
                                    {item.year}
                                </MenuItem>
                            ))
                        )}
                    </Select>
                </FormControl>
            </Box>

            {/* Right section with bar chart */}
            <Box sx={{ width: "55%", height: "100%", mb: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={filledYearlyCountsList} barCategoryGap="30%" margin={{ bottom: 0 }}>
                        <XAxis
                            dataKey="year"
                            tick={{ fontSize: 12, fill: "#666" }}
                            axisLine={false}
                            tickLine={false}
                        />
                        {/* <Tooltip content={<CustomTooltip />} /> */}
                        <Bar dataKey="count" fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </Box>
        </Box>
    );
};


const CustomTooltip = ({ active, payload }: { active?: boolean, payload?: any[] }) => {
    if (active && payload && payload.length) {
        return (
            <div style={{
                background: "white",
                padding: "8px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                fontSize: "14px",
                color: "#333",
            }}>
                <p>{`Year: ${payload[0].payload.year}`}</p>
                <p>{`Count: ${payload[0].value}`}</p>
            </div>
        );
    }

    return null;
};

export default DroneDataYearDisplay;